﻿import { AlertCircle, ArrowLeft, Loader2, Trash2 } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useState } from "react";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";
import type { CreatePromotionRequest } from "@ticketnjoy/types";

import { DashboardPage } from "@/components/dashboard-page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  useDeletePromotionMutation,
  usePromotionQuery,
  usePromotionRedemptionsQuery,
  useUpdatePromotionMutation,
} from "@/features/promotions/api";
import { PromotionForm } from "@/features/promotions/promotion-form";

export function PromotionDetailPage() {
  return (
    <DashboardPage>
      <PromotionDetailView />
    </DashboardPage>
  );
}

function PromotionDetailView() {
  const t = useT();
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const id = params?.id ?? "";

  const { data, isLoading, error } = usePromotionQuery(id);
  const updateMutation = useUpdatePromotionMutation(id);
  const deleteMutation = useDeletePromotionMutation();

  const onSubmit = async (payload: CreatePromotionRequest) => {
    try {
      await updateMutation.mutateAsync(payload);
      toast.success(t("promotions.updateSuccess"));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("common.unexpectedError"));
    }
  };

  async function handleDelete() {
    if (!data) return;
    const ok = window.confirm(t("promotions.confirmDelete"));
    if (!ok) return;
    try {
      await deleteMutation.mutateAsync(data.id);
      toast.success(t("promotions.deleteSuccess"));
      navigate("/promotions");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("common.unexpectedError"));
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-16 text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/promotions">
            <ArrowLeft className="mr-1 h-4 w-4" /> {t("promotions.back")}
          </Link>
        </Button>
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
            <AlertCircle className="h-8 w-8 text-destructive" />
            <p className="text-sm text-muted-foreground">
              {t("promotions.notFound")}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/promotions">
            <ArrowLeft className="mr-1 h-4 w-4" /> {t("promotions.back")}
          </Link>
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleDelete}
          disabled={deleteMutation.isPending}
          className="text-destructive hover:bg-destructive/10 hover:text-destructive"
        >
          <Trash2 className="mr-1 h-4 w-4" />
          {t("promotions.delete")}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle>{data.name}</CardTitle>
              <CardDescription className="mt-1 flex items-center gap-2">
                {data.code ? (
                  <Badge variant="outline" className="font-mono">
                    {data.code}
                  </Badge>
                ) : (
                  <Badge variant="secondary">{t("promotions.automatic")}</Badge>
                )}
                <span>
                  {t("promotions.usage")}: {data.usageCount}
                  {data.maxUses ? ` / ${data.maxUses}` : ""}
                </span>
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <PromotionForm
            mode="edit"
            initial={data}
            onSubmit={onSubmit}
            isSubmitting={updateMutation.isPending}
          />
        </CardContent>
      </Card>

      <RedemptionsCard promotionId={id} />
    </div>
  );
}

function RedemptionsCard({ promotionId }: { promotionId: string }) {
  const t = useT();
  const [enabled, setEnabled] = useState(false);
  const { data, isLoading, error } = usePromotionRedemptionsQuery(
    enabled ? promotionId : "",
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base">
              {t("promotions.redemptionsTitle")}
            </CardTitle>
            <CardDescription>
              {t("promotions.redemptionsDesc")}
            </CardDescription>
          </div>
          {!enabled ? (
            <Button size="sm" variant="outline" onClick={() => setEnabled(true)}>
              {t("promotions.loadRedemptions")}
            </Button>
          ) : null}
        </div>
      </CardHeader>
      {enabled ? (
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center py-8 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : error ? (
            <div className="flex items-center gap-2 p-4 text-sm text-destructive">
              <AlertCircle className="h-4 w-4" />
              {t("promotions.loadError")}
            </div>
          ) : !data || data.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-muted-foreground">
              {t("promotions.noRedemptions")}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium">
                      {t("promotions.redemptionCols.order")}
                    </th>
                    <th className="px-4 py-2 text-left font-medium">
                      {t("promotions.redemptionCols.code")}
                    </th>
                    <th className="px-4 py-2 text-right font-medium">
                      {t("promotions.redemptionCols.discount")}
                    </th>
                    <th className="px-4 py-2 text-right font-medium">
                      {t("promotions.redemptionCols.free")}
                    </th>
                    <th className="px-4 py-2 text-left font-medium">
                      {t("promotions.redemptionCols.date")}
                    </th>
                    <th className="px-4 py-2 text-left font-medium">
                      {t("promotions.redemptionCols.status")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((r) => (
                    <tr key={r.id} className="border-t">
                      <td className="px-4 py-2">
                        <Link
                          to={`/orders/${r.orderNumber}`}
                          className="font-mono text-xs text-primary hover:underline"
                        >
                          {r.orderNumber}
                        </Link>
                      </td>
                      <td className="px-4 py-2 font-mono text-xs">
                        {r.codeUsed ?? "—"}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {r.discountAmount.toFixed(2)}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {r.freeQuantity}
                      </td>
                      <td className="px-4 py-2 text-xs text-muted-foreground">
                        {formatDT(r.redeemedAt)}
                      </td>
                      <td className="px-4 py-2">
                        {r.isReversed ? (
                          <Badge variant="destructive">
                            {t("promotions.reversed")}
                          </Badge>
                        ) : (
                          <Badge variant="success">
                            {t("promotions.active")}
                          </Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      ) : null}
    </Card>
  );
}

const DT_FMT = new Intl.DateTimeFormat("tr-TR", {
  day: "2-digit",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: "Europe/Istanbul",
});

function formatDT(iso: string) {
  return DT_FMT.format(new Date(iso));
}
